import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import {
  createResearchArea,
  deleteResearchArea,
  getResearchAreas,
  updateResearchArea,
} from "../../services/researchAreaService";
import TextArea from "../../components/Inputs/TextAreaInput";
import { researchAreaSchema } from "../../validators/researchAreaValidators";

const ResearchArea = () => {
  const [researchAreas, setResearchAreas] = useState([]);
  const [filteredResearchAreas, setFilteredResearchAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [researchAreaToEdit, setResearchAreaToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [researchAreaToDelete, setResearchAreaToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadResearchAreas();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = researchAreas.filter((item) =>
        item?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResearchAreas(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, researchAreas]);

  const loadResearchAreas = async () => {
    setLoading(true);
    try {
      const { data } = await getResearchAreas();
      setResearchAreas(data.data);
      setFilteredResearchAreas(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load research areas"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (researchAreaToEdit) {
      onUpdateResearchArea(payload, resetForm);
    } else {
      onCreateResearchArea(payload, resetForm);
    }
  };

  const onCreateResearchArea = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createResearchArea(payload);

      setResearchAreas((prev) => [...prev, data.data]);

      toast.success("Research areas added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Research area was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setResearchAreaToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateResearchArea = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalResearchAreas = [...researchAreas];

      const researchAreaIndex = originalResearchAreas.findIndex(
        (researchArea) => researchArea.id === researchAreaToEdit.id
      );

      if (researchAreaIndex === -1)
        throw new Error("Research areas not found.");

      const { data } = await updateResearchArea(researchAreaToEdit.id, payload);

      originalResearchAreas[researchAreaIndex] = data.data;

      setResearchAreas(originalResearchAreas);

      setResearchAreaToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Research areas updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Research areas not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setResearchAreaToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteResearchArea = async () => {
    const originalResearchAreas = [...researchAreas];

    const newResearchAreas = originalResearchAreas.filter(
      (researchArea) => researchArea.id !== researchAreaToDelete.id
    );

    setResearchAreas(newResearchAreas);

    try {
      await deleteResearchArea(researchAreaToDelete?.id);

      toast.success("Research area deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message || error?.response?.data?.error ||
          "Research area not deleted, try again later"
        }`
      );

      setResearchAreas(originalResearchAreas);
    } finally {
      setIsDeleteModalOpen(false);

      setResearchAreaToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setResearchAreaToEdit(null);
    setResearchAreaToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Research Area",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Research Area">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Research Area">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Research Areas" subtitle1="Research Areas" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Research Areas"
            count={filteredResearchAreas.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Research Area"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredResearchAreas.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {researchAreaToEdit
                    ? "Edit Research Area"
                    : "Add Research Area"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: researchAreaToEdit ? researchAreaToEdit?.name : "",
                description: researchAreaToEdit
                  ? researchAreaToEdit?.description
                  : "",
              }}
              enableReinitialize
              validationSchema={researchAreaSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Reseach Area"
                      placeholder="Renewable Energy Innovations"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="Exploring new technologies to enhance the efficiency of solar power systems in East Africa."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteResearchArea}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default ResearchArea;
